import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const getCachedUser = () => {
  try {
    const cached = localStorage.getItem('user');
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  // Hydrate user instantly from cache so a logged-in user never appears logged-out on page load
  const [user, setUser] = useState(() => (localStorage.getItem('auth_token') ? getCachedUser() : null));
  const [loading, setLoading] = useState(() => !!localStorage.getItem('auth_token') && !getCachedUser());

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const clearSession = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Fetch/refresh current user on mount or when token changes
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        setUser(null);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/user/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data && response.data.success) {
          setUser(response.data.profile);
          localStorage.setItem('user', JSON.stringify(response.data.profile));
        }
        // Non-success payload without a 401 status: keep cached session as-is
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          // Token genuinely invalid or expired - log out
          console.warn('AuthContext: Session expired or invalid, logging out');
          clearSession();
        } else {
          // Network error / server hiccup - KEEP the session, fall back to cached user
          console.warn('AuthContext: Profile refresh failed (transient), keeping session:', error.message);
          const cached = getCachedUser();
          if (cached) setUser(cached);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token, BACKEND_URL]);

  // Check URL params for auth callback (run once on mount)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get('auth');
    const authToken = params.get('token');
    const errorMessage = params.get('message');

    if (authStatus === 'success' && authToken) {
      localStorage.setItem('auth_token', authToken);
      setToken(authToken);
      sessionStorage.setItem('riq_welcome', params.get('new_user') === '1' ? 'new' : 'back');

      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      } else {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else if (authStatus === 'error') {
      console.error('AuthContext: Authentication error:', errorMessage);
      setTimeout(() => {
        toast.error('Sign-in failed', {
          description: errorMessage || 'LinkedIn authentication failed. Please try again.',
          duration: 8000
        });
      }, 300);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []); // Run only once on mount

  // Claim pending referral once authenticated
  useEffect(() => {
    const refCode = localStorage.getItem('riq_ref');
    if (user && token && refCode) {
      axios.post(`${BACKEND_URL}/api/recruiter/referral/claim`, { ref_code: refCode }, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {}).finally(() => localStorage.removeItem('riq_ref'));
    }
  }, [user, token, BACKEND_URL]);

  const login = () => {
    // Remember where the user was so we can return them after LinkedIn OAuth
    if (!sessionStorage.getItem('redirectAfterLogin')) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    }
    window.location.href = `${BACKEND_URL}/api/auth/linkedin`;
  };

  const logout = () => {
    clearSession();
  };

  const updateProfile = async (profileData) => {
    if (!token) return { success: false, message: 'Not authenticated' };

    try {
      const response = await axios.put(`${BACKEND_URL}/api/user/profile`, profileData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data && response.data.success) {
        setUser(response.data.profile);
        localStorage.setItem('user', JSON.stringify(response.data.profile));
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.detail || 'Update failed' };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Update failed. Please try again.'
      };
    }
  };

  const refreshUserStats = async () => {
    if (!token) return null;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return null;
    }
  };

  const checkSearchLimit = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/check-limit`);
      return response.data;
    } catch (error) {
      console.error('Error checking search limit:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,          // LinkedIn OAuth login
    logout,
    updateProfile,
    refreshUserStats,
    checkSearchLimit,
    isAuthenticated: !!user,
    isPremium: user?.subscription_status === 'premium'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
